import { MetadataDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveContentBlocks,
  resolveDateObject,
  resolveId,
  resolveImage,
  resolveAsset,
  resolvePerson,
  resolveRTC,
  resolveSimpleContent,
  resolveTheme,
} from '@/utils/resolvers';
import resolveImageUrl from '@/utils/resolvers/resolveImageUrl';
import React, { useMemo, useRef } from 'react';
import CMSWrapper from '../shared/CMSWrapper';
import { withGTM } from '../shared/HOCs';
import SEOHrefLang from '../shared/SEOHrefLang';
import PressRelease from './PressRelease';
import { onDatalayerSocialShare } from './PressRelease.datalayer';

const MappedPressRelease = ({ content, config, labels, contentButtons, pageModels }: any) => {
  const translationConfig = pageModels?.config?.translation;
  const { backgroundColor, downloadIcon } = config;
  const downloadIconPath = useMemo(() => resolveImageUrl({ raw: downloadIcon }), [downloadIcon]);
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: backgroundColor }),
    [backgroundColor],
  );
  const pressReleaseContent = useMemo(
    () => ({
      ...content,
      ...config,
      contentButtons,
      downloadIconPath,
    }),
    [config, content, contentButtons, downloadIconPath],
  );
  const hrefLangProps = React.useMemo(
    () => ({
      hrefLangsRaw: content?.pageMetadata?.hreflangUrl,
      exclusionPaths: translationConfig?.endpoint_exclusion_paths,
      countryCodesWhitelist: translationConfig?.country_codes_whitelist,
    }),
    [
      content?.pageMetadata?.hreflangUrl,
      translationConfig?.endpoint_exclusion_paths,
      translationConfig?.country_codes_whitelist,
    ],
  );

  return (
    <div data-datalayer-position="press_release">
      <PressRelease theme={theme} {...pressReleaseContent} labels={labels} />
      <SEOHrefLang {...hrefLangProps} />
    </div>
  );
};

const resolveGallery = ({ rawVal, page }: any) => {
  return rawVal && rawVal.map((img: any) => resolveImage({ rawVal: img, page }));
};

const resolveContacts = ({ rawVal, page }: any) => {
  return (
    rawVal &&
    rawVal.map((media: any) => {
      return {
        section: media.section,
        region: media.region,
        mediaContact: media.mediaContact.map((contact: any) => {
          return resolvePerson({ rawVal: contact, page });
        }),
      };
    })
  );
};

const MappedSocialShareEvents = withGTM({
  Component: MappedPressRelease,
  onDatalayer: onDatalayerSocialShare,
  config: {
    eventName: 'social-share-event',
    isCustom: true,
  },
});

const resolveDescriptiveContent = ({ rawVal, page, pageModels }: any) => {
  return rawVal.map((val: any) => resolveSimpleContent({ rawVal: val, page, pageModels }));
};

const PressReleaseCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        title: null,
        shortDescription: [null, resolveRTC],
        location: null,
        publicationDate: [null, resolveDateObject],
        relatedLinks: [null, resolveRTC],
        localeString: null,
        includeBreadcrumb: [null, resolveBool],
        imageCarousel: ['images', resolveGallery],
        mediaContacts: [null, resolveContacts],
        pdf: [null, resolveAsset],
        includeDownloadOpt: [null, resolveBool],
        detailContent: [null, resolveContentBlocks],
        legacyContent: [null, resolveRTC],
        includeSocialShare: ['displaySocialShare', resolveBool],
        descriptiveContent: [null, resolveDescriptiveContent],
        pageMetadata: [null, MetadataDocumentMapping],
        cardTileImage: ['cardImage', resolveImage],
        keys: null,
        id: [null, resolveId],
        contentcategory: 'contentCategory',
      },
    ],
    'pressReleasePage.config': [
      'config',
      {
        download_icon: 'downloadIcon',
        background_color: 'backgroundColor',
      },
    ],
    'pressReleasePage.labels': 'labels',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedSocialShareEvents}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default PressReleaseCMSWrapper;
